import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import type { BoardPrepApplication, BoardPrepReport, ComplaintSummary } from '@/types/models';
import { formatBusinessDate, formatBusinessDateLong } from '@/utils/date-utils';
import { isMessageStatus, MESSAGE_STATUS_LABEL } from '@/utils/inbox-triage';

/**
 * The board-meeting prep sheet (#566) — ONE renderer for the in-app page and the print route.
 *
 * Pure: it is handed the report and draws it. The page adds the "Mark reported" actions by
 * passing `onMarkReported`; the print route passes nothing, so paper never shows a button.
 * Every date here is a business date (ET day as a midnight-UTC marker), so it is printed
 * with `formatBusinessDate*`, which reads UTC fields.
 */

export interface BoardPrepSheetProps {
	report: BoardPrepReport;
	/** Row action for complaints not yet reported; omitted on paper. */
	onMarkReported?: (complaintId: string) => void;
	/** Open a row in the app; omitted on paper. */
	onOpenApplication?: (id: string) => void;
	onOpenComplaint?: (id: string) => void;
	busyId?: string | null;
}

const lotLabel = (accountNumber: string | null, ownerName: string | null): string | null =>
	[accountNumber, ownerName].filter(Boolean).join(' · ') || null;

const statusLabel = (status: string): string => (isMessageStatus(status) ? MESSAGE_STATUS_LABEL[status] : status);

const Section: React.FC<{ title: string; hint: string; count: number; testID: string; children: React.ReactNode }> = ({
	title, hint, count, testID, children,
}) => (
	<View style={styles.section} testID={testID}>
		<Text style={styles.sectionTitle}>{title} ({count})</Text>
		<Text style={styles.sectionHint}>{hint}</Text>
		{count === 0 ? <Text style={styles.empty}>None.</Text> : children}
	</View>
);

const ApplicationRow: React.FC<{ app: BoardPrepApplication; onOpen?: (id: string) => void }> = ({ app, onOpen }) => {
	const body = (
		<>
			<Text style={styles.rowTitle}>
				{app.applicationNumber != null ? `#${app.applicationNumber} · ` : ''}{app.requestTypeName || 'Improvement request'}
			</Text>
			<Text style={styles.rowMeta}>
				{[
					lotLabel(app.accountNumber, app.accountOwnerName) ?? app.submitterName,
					app.propertyAddress,
					`Received ${formatBusinessDate(app.receivedOn)}`,
					app.boardMeetingDate != null ? `Meeting ${formatBusinessDate(app.boardMeetingDate)}` : null,
				].filter(Boolean).join(' · ')}
			</Text>
			{app.description ? <Text style={styles.rowBody}>{app.description}</Text> : null}
		</>
	);
	return onOpen ? (
		<Pressable style={styles.row} onPress={() => onOpen(app.id)} testID={`board-prep-application-${app.id}`}>{body}</Pressable>
	) : (
		<View style={styles.row} testID={`board-prep-application-${app.id}`}>{body}</View>
	);
};

const ComplaintRowView: React.FC<{
	complaint: ComplaintSummary;
	onOpen?: (id: string) => void;
	onMarkReported?: (id: string) => void;
	busy: boolean;
}> = ({ complaint, onOpen, onMarkReported, busy }) => {
	const against = complaint.against
		.map((t) => lotLabel(t.accountNumber, t.ownerName) ?? t.address ?? 'Unit')
		.join('; ');
	const body = (
		<View style={{ flex: 1, minWidth: 0 }}>
			<Text style={styles.rowTitle}>{complaint.subject || 'Complaint'}</Text>
			<Text style={styles.rowMeta}>
				{[
					`From ${complaint.complainantName || 'unknown'}${complaint.complainantAccountNumber ? ` (${complaint.complainantAccountNumber})` : ''}`,
					`Received ${formatBusinessDate(complaint.receivedOn)}`,
					statusLabel(complaint.status),
					complaint.reportedToBoardOn != null ? `Reported ${formatBusinessDate(complaint.reportedToBoardOn)}` : null,
				].filter(Boolean).join(' · ')}
			</Text>
			<Text style={styles.rowAgainst}>Against: {against || 'no units named'}</Text>
			{complaint.excerpt ? <Text style={styles.rowBody}>{complaint.excerpt}</Text> : null}
		</View>
	);
	return (
		<View style={[styles.row, styles.rowWithAction]} testID={`board-prep-complaint-${complaint.id}`}>
			{onOpen ? (
				<Pressable style={{ flex: 1, minWidth: 0 }} onPress={() => onOpen(complaint.id)}>{body}</Pressable>
			) : body}
			{onMarkReported && complaint.reportedToBoardOn == null ? (
				<Pressable
					onPress={() => onMarkReported(complaint.id)}
					disabled={busy}
					style={[styles.action, busy && { opacity: 0.6 }]}
					testID={`btn-mark-reported-${complaint.id}`}
				>
					<Text style={styles.actionLabel}>{busy ? 'Saving…' : 'Mark reported'}</Text>
				</Pressable>
			) : null}
		</View>
	);
};

export const BoardPrepSheet: React.FC<BoardPrepSheetProps> = ({
	report, onMarkReported, onOpenApplication, onOpenComplaint, busyId = null,
}) => {
	const { open, reportedStillOpen, addressedUnreported } = report.complaints;
	const complaintRows = (list: ComplaintSummary[]) => list.map((c) => (
		<ComplaintRowView
			key={c.id}
			complaint={c}
			onOpen={onOpenComplaint}
			onMarkReported={onMarkReported}
			busy={busyId === c.id}
		/>
	));
	return (
		<View testID="board-prep-sheet">
			<View style={styles.header}>
				<Text style={styles.assocName}>{report.associationName}</Text>
				<Text style={styles.reportTitle}>Board Meeting Prep</Text>
				<Text style={styles.asOf}>Prepared {formatBusinessDateLong(report.generatedOn)}</Text>
			</View>

			<Section
				title="Exterior improvements waiting for board approval"
				hint="Complete packages sent to the board and not yet decided."
				count={report.applications.length}
				testID="board-prep-applications"
			>
				{report.applications.map((app) => <ApplicationRow key={app.id} app={app} onOpen={onOpenApplication} />)}
			</Section>

			<Section
				title="Open complaints — not yet reported"
				hint="Still open (or waiting on the homeowner) and not yet in front of the board."
				count={open.length}
				testID="board-prep-complaints-open"
			>
				{complaintRows(open)}
			</Section>

			<Section
				title="Reported to the board — still open"
				hint="Raised at an earlier meeting and not yet resolved."
				count={reportedStillOpen.length}
				testID="board-prep-complaints-reported"
			>
				{complaintRows(reportedStillOpen)}
			</Section>

			<Section
				title="Addressed, but never reported to the board"
				hint="Dealt with and archived, but the board has not been told."
				count={addressedUnreported.length}
				testID="board-prep-complaints-addressed"
			>
				{complaintRows(addressedUnreported)}
			</Section>
		</View>
	);
};

const styles = StyleSheet.create({
	header: { alignItems: 'center', marginBottom: 18 },
	assocName: { fontSize: 16, fontWeight: '700', color: '#000' },
	reportTitle: { fontSize: 18, fontWeight: '700', color: '#000', marginTop: 4 },
	asOf: { fontSize: 13, color: '#000', marginTop: 2 },
	section: { marginBottom: 20 },
	sectionTitle: { fontSize: 14, fontWeight: '700', color: '#212121', borderBottomWidth: 1.5, borderColor: '#000', paddingBottom: 4 },
	sectionHint: { fontSize: 11, color: '#607d8b', marginTop: 4, marginBottom: 4 },
	empty: { fontSize: 12, color: '#90a4ae', fontStyle: 'italic', paddingVertical: 8 },
	row: { paddingVertical: 8, borderBottomWidth: 0.5, borderColor: '#cfd8dc' },
	rowWithAction: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
	rowTitle: { fontSize: 13, fontWeight: '700', color: '#000' },
	rowMeta: { fontSize: 11, color: '#455a64', marginTop: 2 },
	rowAgainst: { fontSize: 11, color: '#ad1457', fontWeight: '600', marginTop: 2 },
	rowBody: { fontSize: 11, color: '#212121', marginTop: 4 },
	action: {
		paddingVertical: 6, paddingHorizontal: 10, borderRadius: 6,
		borderWidth: 1, borderColor: '#1976d2', backgroundColor: '#fff',
	},
	actionLabel: { color: '#1976d2', fontSize: 12, fontWeight: '600' },
});

export default BoardPrepSheet;
